import React, { useState } from 'react';
import { Link as RouterLink, useMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, Collapse, ListItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const itemSx = {
  display: 'block',
  paddingTop: 0,
  paddingBottom: 0,
};

const itemLeafSx = {
  display: 'flex',
  paddingTop: 0,
  paddingBottom: 0,
};

const StyledButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: '10px 8px',
  justifyContent: 'flex-start',
  textTransform: 'none',
  letterSpacing: 0,
  width: '100%',
  '& .Nav-title': {
    marginRight: 'auto',
  },
  '& .Nav-icon': {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1),
  },
}));

const StyledLeafButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: '10px 8px',
  justifyContent: 'flex-start',
  textTransform: 'none',
  letterSpacing: 0,
  width: '100%',
  fontWeight: theme.typography.fontWeightRegular,
  '&.depth-0 .Nav-title': {
    fontWeight: theme.typography.fontWeightMedium,
  },
  '& .Nav-title': {
    marginRight: 'auto',
  },
  '& .Nav-icon': {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1),
  },
  '&.Nav-active': {
    color: theme.palette.secondary.main,
    '& .Nav-title': {
      fontWeight: theme.typography.fontWeightMedium,
    },
    '& .Nav-icon': {
      color: theme.palette.secondary.main,
    },
  },
}));

const NavItem = ({
  children,
  className,
  depth,
  href,
  icon: Icon,
  info: Info,
  open: openProp = false,
  title,
  ...rest
}) => {
  const [open, setOpen] = useState(openProp);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  let paddingLeft = 8;

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  const style = { paddingLeft };
  const isActive = !!useMatch({ path: href || '', end: true });

  if (children) {
    return (
      <ListItem
        sx={itemSx}
        className={className}
        disableGutters
        key={title}
        {...rest}
      >
        <StyledButton onClick={handleToggle} style={style}>
          {Icon && <Icon className="Nav-icon" size="20" />}
          <span className="Nav-title">{title}</span>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </StyledButton>
        <Collapse in={open}>{children}</Collapse>
      </ListItem>
    );
  }

  return (
    <ListItem
      sx={itemLeafSx}
      className={className}
      disableGutters
      key={title}
      {...rest}
    >
      <StyledLeafButton
        className={`depth-${depth}${isActive ? ' Nav-active' : ''}`}
        component={RouterLink}
        style={style}
        to={href}
      >
        {Icon && <Icon className="Nav-icon" size="20" />}
        <span className="Nav-title">{title}</span>
        {Info && <Info />}
      </StyledLeafButton>
    </ListItem>
  );
};

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.elementType,
  info: PropTypes.elementType,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
};

export default NavItem;
